import { useCallback, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export interface ConfirmOptions {
  title: string
  description: string
  confirmLabel: string
  /** For actions that throw work away. */
  destructive?: boolean
}

/**
 * The app's own confirmation in place of the browser's. Render `dialog` once;
 * `await confirm(...)` is true when the professor confirms.
 */
export function useConfirm() {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  const resolver = useRef<((ok: boolean) => void) | null>(null)

  const confirm = useCallback(
    (next: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        resolver.current = resolve
        setOptions(next)
        setOpen(true)
      }),
    []
  )

  const close = (ok: boolean) => {
    resolver.current?.(ok)
    resolver.current = null
    setOpen(false)
  }

  const dialog = (
    <Dialog open={open} onOpenChange={(next) => !next && close(false)}>
      <DialogContent className='sm:max-w-md' data-testid='world-confirm'>
        <DialogHeader>
          <DialogTitle className='text-dark-blue'>{options?.title}</DialogTitle>
          <DialogDescription>{options?.description}</DialogDescription>
        </DialogHeader>
        <DialogFooter className='gap-2'>
          <Button variant='outline' onClick={() => close(false)}>
            Cancel
          </Button>
          <Button
            data-testid='world-confirm-ok'
            variant={options?.destructive ? 'destructive' : 'default'}
            className={
              options?.destructive
                ? undefined
                : 'bg-dark-blue hover:bg-dark-blue-hovered'
            }
            onClick={() => close(true)}
          >
            {options?.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  return { confirm, dialog }
}
